/*
 * BONQUERÉ — Legal document contents rail  (BNQ-WEB-0291 / 0292 · Sprint 3.4.9;
 *                                           heading promotion BNQ-WEB-0308 · Sprint 3.4.10)
 *
 * Progressive enhancement shared by EVERY branded legal page — Privacy (3.4.9), Shipping Policy and
 * Refund Policy (3.4.10), and any further policy built on the same shell (DEC-0111). It derives the
 * left-hand contents rail from the headings that Shopify actually rendered inside the canonical policy
 * body (`{{ shop.<policy>_policy.body }}`), so the navigation can never drift from, or point past, the
 * live legal content (DEC-0108). Nothing here is policy-specific: the script reads whatever body the
 * page rendered, so the same file serves all three pages with no per-page branch.
 *
 * What it does, and nothing more:
 *   0. Where a body has NO authored headings but numbers its sections as plain paragraphs, promotes
 *      those title paragraphs to real <h2> verbatim, so the rail and heading structure exist at all
 *      (gated and fail-safe — see promoteNumberedHeadings; a no-op on authored-heading policies).
 *   1. Reads the H2 headings inside [data-legal-body], assigns each a stable, collision-safe id
 *      (preserving any valid id already present), and builds the rail links inside [data-legal-toc].
 *   2. Smooth-scrolls to a section on click, honouring the sticky-header offset (via CSS
 *      scroll-margin-top on the headings) and prefers-reduced-motion, then moves focus to the target
 *      heading so keyboard and screen-reader users land where sighted users do.
 *   3. Tracks the section in view with an IntersectionObserver and marks the matching rail link active.
 *   4. Reveals and wires the "Print / Save as PDF" control (it calls window.print() — DEC-0109).
 *
 * With JavaScript disabled NOTHING here is required to read the policy: the rail simply stays empty
 * (its container is hidden until populated), the print button stays hidden, and the full legal body
 * renders and scrolls normally. No polling, no third-party library, scoped to this page only.
 */
(function () {
  'use strict';

  var body = document.querySelector('[data-legal-body]');
  if (!body) return;

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Stable, collision-safe ids ──────────────────────────────────────────────────────────────
     A heading may already carry a valid id (e.g. if a future policy body is authored with anchors);
     that is preserved. Otherwise the id is slugified from the heading text, with a numeric suffix if
     the same text appears twice, so the rail can never produce a duplicate or dead anchor. */
  function slugify(text) {
    return String(text)
      .toLowerCase()
      .replace(/['’"]/g, '') // drop apostrophes/quotes so "who we're" reads cleanly
      .replace(/[^a-z0-9]+/g, '-') // any run of non-alphanumerics → single hyphen
      .replace(/^-+|-+$/g, ''); // trim leading/trailing hyphens
  }

  var used = {};
  function uniqueId(base) {
    var candidate = base || 'section';
    var n = 2;
    while (used[candidate] || document.getElementById(candidate)) {
      candidate = (base || 'section') + '-' + n;
      n += 1;
    }
    used[candidate] = true;
    return candidate;
  }

  /* ── Heading promotion for flat, numbered policy bodies (BNQ-WEB-0308 · Sprint 3.4.10) ────────
     Some Admin policies are authored with real headings and some are not. The Privacy policy (which
     Shopify generated from its template) uses proper <h2> elements. The hand-written Shipping and
     Refund policies are FLAT: every block is a <p>, and each section title is an ordinary paragraph
     reading "1. Shipping destinations", "2. Order processing time", … With no <h2> in the body there
     is nothing to derive a rail from, the document has no heading structure for assistive technology,
     and the numbered-section presentation cannot engage.

     Rather than hard-code labels (which the brief forbids and which would drift from Admin), this
     promotes those title paragraphs to real <h2> elements before the rail is built. The text is moved
     VERBATIM — innerHTML is transferred untouched, including the "N." prefix — so no legal wording is
     altered, added or removed; only the element that wraps it changes. With JavaScript off the body
     renders exactly as Shopify emitted it.

     It is deliberately conservative, and FAILS SAFE (returns without promoting anything) rather than
     risk turning a real paragraph into a heading:
       • it does nothing at all if the body already contains an authored <h2> — authored headings
         always win, so the Privacy page is provably untouched by this code path;
       • only paragraphs of the document's own prose flow are considered. A paste from Word or Google
         Docs nests everything inside wrapper divs (the live Refund policy is three divs deep), so
         depth alone cannot be the test; instead any paragraph inside a list, table or quotation is
         excluded, which is what actually distinguishes a section title from quoted or tabulated copy;
       • a candidate must be a short title, not a sentence: "N." + up to 79 characters, with no
         terminal .,:; and no mid-string sentence break;
       • the candidates must form a COMPLETE, CONSECUTIVE run 1…N of at least three sections. A body
         whose numbers skip, repeat or start elsewhere is not a numbered legal document, and nothing
         is promoted.
     If the owner later authors real headings in Admin, this becomes an automatic no-op. */
  function promoteNumberedHeadings(container) {
    if (container.querySelector('h2')) return;

    var SECTION_TITLE = /^(\d{1,2})\.\s+(\S.{1,79})$/;
    var EXCLUDED_ANCESTOR = 'li,ul,ol,table,blockquote';
    var candidates = [];
    var paragraphs = container.querySelectorAll('p');

    for (var i = 0; i < paragraphs.length; i += 1) {
      var el = paragraphs[i];
      // Quoted, listed and tabulated copy is never a section title, however it is nested. The match
      // is bounded to the policy body so an ancestor outside it can never disqualify a paragraph.
      var excluded = el.closest(EXCLUDED_ANCESTOR);
      if (excluded && container.contains(excluded)) continue;

      var match = (el.textContent || '').replace(/\s+/g, ' ').trim().match(SECTION_TITLE);
      if (!match) continue;

      var title = match[2];
      if (/[.,:;]$/.test(title) || /\.\s/.test(title)) continue;

      candidates.push({ el: el, number: parseInt(match[1], 10) });
    }

    if (candidates.length < 3) return;
    for (var j = 0; j < candidates.length; j += 1) {
      if (candidates[j].number !== j + 1) return;
    }

    candidates.forEach(function (candidate) {
      var heading = document.createElement('h2');
      heading.innerHTML = candidate.el.innerHTML;
      // Records the document's own section number, and marks the heading so the body's CSS counter
      // stands down — the numeral is already part of the promoted text.
      heading.setAttribute('data-legal-promoted', String(candidate.number));
      candidate.el.parentNode.replaceChild(heading, candidate.el);
    });
  }

  promoteNumberedHeadings(body);

  var headings = Array.prototype.slice.call(body.querySelectorAll('h2'));
  var nav = document.querySelector('[data-legal-toc]');

  // Nothing to navigate: leave the rail hidden (it starts hidden) and stop. The body still reads.
  if (!headings.length || !nav) {
    wirePrint();
    return;
  }

  var links = [];
  var linkById = {};
  var list = document.createElement('ul');
  list.className = 'bnq-legal-nav__list';
  list.setAttribute('role', 'list');

  headings.forEach(function (heading, index) {
    /* The rail label and numeral. For an AUTHORED heading the label is the heading text and the
       numeral is its position (Privacy: "01 · Who we are"). For a PROMOTED heading the number is
       already in the text, so it is lifted into the numeral column and stripped from the label —
       giving the same "01 · Shipping destinations" rail instead of a doubled "01 · 1. Shipping
       destinations". The numeral then reflects the document's OWN section number rather than an
       index, which is what a reader is looking for. The heading in the body keeps its full text. */
    var promoted = heading.getAttribute('data-legal-promoted');
    var labelText = heading.textContent.trim();
    var numText;

    if (promoted) {
      labelText = labelText.replace(/^\d{1,2}\.\s*/, '');
      numText = (promoted.length < 2 ? '0' : '') + promoted;
    } else {
      // Two-digit index mirroring the HiFi's 01, 02 … numerals; purely presentational.
      numText = (index + 1 < 10 ? '0' : '') + (index + 1);
    }

    var existing = heading.getAttribute('id');
    // Slugged from the label, so a promoted section anchors as #shipping-destinations rather than
    // #1-shipping-destinations.
    var id = existing && existing.length ? existing : uniqueId(slugify(labelText));
    heading.setAttribute('id', id);
    if (existing) used[id] = true;

    var li = document.createElement('li');
    li.className = 'bnq-legal-nav__item';

    var a = document.createElement('a');
    a.className = 'bnq-legal-nav__link';
    a.href = '#' + id;

    var num = document.createElement('span');
    num.className = 'bnq-legal-nav__num';
    num.setAttribute('aria-hidden', 'true');
    num.textContent = numText;

    var label = document.createElement('span');
    label.className = 'bnq-legal-nav__text';
    label.textContent = labelText;

    a.appendChild(num);
    a.appendChild(label);
    li.appendChild(a);
    list.appendChild(li);

    a.addEventListener('click', onLinkClick);

    links.push(a);
    linkById[id] = a;
  });

  nav.appendChild(list);
  nav.hidden = false;

  // Reveal the rail's "Contents" heading only now that there is something under it.
  var railHeading = document.querySelector('[data-legal-toc-heading]');
  if (railHeading) railHeading.hidden = false;

  /* ── Smooth scroll + focus management ────────────────────────────────────────────────────────
     The offset itself is owned by CSS (scroll-margin-top on the headings), so this only chooses the
     behaviour and moves focus. Making the heading focusable with tabindex=-1 lets keyboard users
     continue reading from the section they jumped to, which a plain hash change does not do. */
  function scrollToHeading(heading, push) {
    heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
    if (push && window.history && window.history.pushState) {
      window.history.pushState(null, '', '#' + heading.id);
    }
  }

  function onLinkClick(event) {
    var id = this.getAttribute('href').slice(1);
    var heading = document.getElementById(id);
    if (!heading) return; // let the browser handle anything unexpected
    event.preventDefault();
    scrollToHeading(heading, true);
  }

  /* ── Active-section tracking ─────────────────────────────────────────────────────────────────
     The band between 20% from the top and 30% from the bottom is the "reading line"; whichever
     heading's section sits across it is marked active. Falls back silently if the observer is
     unavailable — the links still work, they just do not self-highlight. */
  function setActive(id) {
    links.forEach(function (a) {
      var isActive = a === linkById[id];
      a.classList.toggle('is-active', isActive);
      if (isActive) {
        a.setAttribute('aria-current', 'true');
      } else {
        a.removeAttribute('aria-current');
      }
    });
  }

  if ('IntersectionObserver' in window) {
    var visible = {};
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          visible[entry.target.id] = entry.isIntersecting;
        });
        // Choose the first heading (document order) currently in the reading band.
        for (var i = 0; i < headings.length; i += 1) {
          if (visible[headings[i].id]) {
            setActive(headings[i].id);
            return;
          }
        }
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    headings.forEach(function (heading) {
      observer.observe(heading);
    });
  }

  /* ── Deep links ──────────────────────────────────────────────────────────────────────────────
     The ids are injected by this script, so a #hash present at load may have been ignored by the
     browser before the ids existed. Re-honour it once, after the rail is built. */
  if (window.location.hash.length > 1) {
    var target = document.getElementById(window.location.hash.slice(1));
    if (target && body.contains(target)) {
      // A frame's delay lets layout settle so the offset lands the heading correctly.
      window.requestAnimationFrame(function () {
        scrollToHeading(target, false);
      });
    }
  }

  wirePrint();

  /* ── Print / Save as PDF (DEC-0109) ──────────────────────────────────────────────────────────
     Hidden until wired so a no-JS visitor never sees a control that cannot act; the print stylesheet
     (in bnq-legal-body.liquid) does the rest. Honest label — it opens the print dialog, from which
     the browser's own "Save as PDF" is available; it does not itself download a file. */
  function wirePrint() {
    var buttons = Array.prototype.slice.call(document.querySelectorAll('[data-legal-print]'));
    buttons.forEach(function (button) {
      button.hidden = false;
      button.addEventListener('click', function (event) {
        event.preventDefault();
        window.print();
      });
    });
  }
})();
